import fromPairs from 'lodash/fromPairs';
import toPairs from 'lodash/toPairs';
import maxBy from 'lodash/maxBy';

import {LOCATION_CHANGE} from 'react-router-redux';
import A from './types';
import RESOURCE from '~/modules/resources/types';
import toggle, {isActive} from '~/reducers/toggle';
import searchTypes from '~/modules/loadsSearch/types';
import getStorageValue from '~/misc/getStorageValue';
import safeJSONParse from '~/misc/safeJSONParse';
import create from '~/misc/create';
import resourceId from '~/misc/resourceId';
import loadFromState from '~/misc/loadFromState';

const isLeavingSearch = (fullState, action) => {
  const current = fullState.routing;
  if (!current.pathname.startsWith('/loads') || !current.search) {
    return false;
  }

  if (action.payload.pathname !== '/loads' || action.payload.search !== current.search) {
    return true;
  }

  return false;
};

const getViewed = (savedData) => {
  if (!savedData) {
    return {
      index: {},
      counter: 0,
    };
  }

  if (Array.isArray(savedData)) {
    const list = savedData.map((item) => [item, 0]);
    return {
      index: fromPairs(list),
      counter: 1,
    };
  }

  const pairs = toPairs(savedData).map(item => {
    const value = parseFloat(item[1]) || 0;
    return [item[0], value];
  });

  const max = maxBy(pairs, '1');

  return {
    index: fromPairs(pairs),
    counter: (max && max[1]) || 1,
  };
};

const init = () => {
  const viewed = getViewed(safeJSONParse(getStorageValue('viewedResults')));
  return {
    toggledTruckStops: {},
    toggledWeightStations: {},
    viewed: viewed.index,
    viewedCounter: viewed.counter || 0,
    active: {},
    activeBooknow: {},
    skippingBooknow: false,
    maps: {},
    mapData: {},
    phoneToggled: {},
    premiumToolTipHidden: Boolean(safeJSONParse(getStorageValue('premiumToolTipHidden'))),
    showSignLoginBtn: false,
    currentId: '',
  };
};

const resetedOpened = (state, actionsQueue) => ({
  active: {},
  activeBooknow: {},
  skippingBooknow: false,
  phoneToggled: {},
  actionsQueue: Object.keys(state.active)
    .map(key => create(A.SEARCH_LOAD_INACTIVE, {identity: state.active[key]}))
    .concat(actionsQueue, state.lastBooknowLoad
      && Object.keys(state.activeBooknow).length && create(A.LOADS_SKIP_BOOKNOW, state.lastBooknowLoad)),
});

const loadURLRegexp = /\/loads\/(.*[^\/])$/;

const matchLoad = (url) => {
  const matched = url.match(loadURLRegexp);
  return matched && resourceId('load', {id: matched[1]});
};

const reduceMapData = (fn) => (action, state) => {
  if (action.error || action.pending) {
    return null;
  }

  const loadId = action.meta.id;

  const loadMapData = {
    ...(state.mapData[loadId] || {}),
    ...fn(action),
  };

  return {
    mapData: {
      ...state.mapData,
      [loadId]: loadMapData,
    },
  };
};

const mapRoute = reduceMapData(({payload}) => payload);
const mapLocations = reduceMapData(({payload}) => ({poiLocations: payload}));
const mapWeightStations = reduceMapData(({payload}) => ({weightStations: payload}));
const mapTruckStops = reduceMapData(({payload}) => ({truckStops: payload}));


const reducer = (state, action, fullState) => {
  switch (action.type) {
    case A.LOADS_BROKER_SHOW_SIGNLOGIN_BTN:
      return {
        showSignLoginBtn: true,
        currentId: action.payload.currentId,
      };
    case A.LOADS_BROKER_HIDE_SIGNLOGIN_BTN:
      return {
        showSignLoginBtn: false,
        currentId: '',
      };
    case A.LOADS_PREMIUM_TOOLTIP_CLOSED:
      return {
        premiumToolTipHidden: true,
      };
    case A.SEARCH_RESULTS_TOGGLE: {
      const identity = action.payload;
      const newState = toggle(state, 'active', identity.mediumkey, identity);
      const load = loadFromState(fullState, identity);
      const bookPart = load.book_now
        ? toggle(state, 'activeBooknow', identity.mediumkey, identity)
        : {};

      const skippingBooknow = !state.skippingBooknow && !load.book_now && Object.keys(state.activeBooknow).length;

      const newCounter = state.viewedCounter + 1;
      return {
        ...newState,
        ...bookPart,
        skippingBooknow: state.skippingBooknow || skippingBooknow,
        viewedCounter: newCounter,
        lastBooknowLoad: load.book_now && load.identity,
        viewed: {
          ...state.viewed,
          [identity.props.id]: newCounter,
        },
        actionsQueue: [
          isActive(newState, 'active', identity.mediumkey)
            ? create(A.SEARCH_LOAD_ACTIVE, {identity: identity})
            : create(A.SEARCH_LOAD_INACTIVE, {identity: identity}),
          state.lastBooknowLoad && skippingBooknow && create(A.LOADS_SKIP_BOOKNOW, state.lastBooknowLoad),
        ],
      };
    }

    case A.LOADS_POINTED: {
      const loadId = action.payload.id;
      const mapData = state.mapData[loadId] || {};

      return {
        mapData: {
          ...state.mapData,
          [loadId]: {
            ...mapData,
            ...action.payload.data,
          },
        },
      };
    }

    case A.SEARCH_RESULTS_STATICMAP_URL_CACHED: {
      return mapRoute(action, state);
    }

    case A.LOADS_FETCHED_ROUTE_LOCATIONS: {
      return mapLocations(action, state);
    }

    case A.LOADS_FETCHED_ROUTE_TRUCK_STOPS: {
      return mapTruckStops(action, state);
    }

    case A.LOADS_FETCHED_ROUTE_WEIGHT_STATIONS: {
      return mapWeightStations(action, state);
    }

    // case A.LOADS_FETCHED_ROUTE_FUEL_PRICES: {
    //   return mapLocations(action, state);
    // }

    case A.LOADS_TOGGLED_ROUTE_WEIGHT_STATIONS: {
      return {
        ...toggle(state, 'toggledWeightStations', action.payload.props.id),
      };
    }

    case A.LOADS_TOGGLED_ROUTE_TRUCK_STOPS: {
      return {
        ...toggle(state, 'toggledTruckStops', action.payload.props.id),
      };
    }

    case A.TOGGLE_LOADPHONE: {
      return {
        ...toggle(state, 'phoneToggled', action.payload.props.id),
      };
    }

    case searchTypes.LOADS_SEARCH:
    case searchTypes.LOADS_SORT: {
      return resetedOpened(state);
    }
    case LOCATION_CHANGE: {
      const oldPath = fullState.routing.pathname;
      const newPath = action.payload.pathname;

      const oldLoad = matchLoad(oldPath);
      const newLoad = matchLoad(newPath);

      const leavingSearch = isLeavingSearch(fullState, action);
      if (!leavingSearch && !newLoad && !oldLoad) {
        return null;
      }

      if (!newLoad && !oldLoad) {
        return resetedOpened(state);
      }

      return {
        ...resetedOpened(state, [
          oldLoad && create(RESOURCE.RESOURCE_NOT_NEEDED, {
            name: 'load',
            need: oldLoad,
            needy: oldLoad,
          }),
          newLoad && create(RESOURCE.RESOURCE_NEEDED, {
            name: 'load',
            need: newLoad,
            needy: newLoad,
          }),
        ].filter(item => item)),
      };
    }
  }

  return null;
};


export default {reducer, init};



// WEBPACK FOOTER //
// ./src/modules/loads/reducer.js